import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, WifiOff } from '../lib/icons';
import { checkSupabaseHealth, dataCache } from '../lib/supabaseHelpers';
import { isSupabaseConfigured, getSupabase } from '../lib/supabase';
import { useLocation } from 'react-router-dom';

export const SupabaseConnectionMonitor: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'healthy' | 'unhealthy'>('checking');
  const [latency, setLatency] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const location = useLocation();

  const checkConnection = async () => {
    // Skip on premium benefits route
    if (location.pathname === '/premium-benefits') {
      setConnectionStatus('healthy');
      setError(null);
      setLatency(null);
      setIsRetrying(false);
      return;
    }

    // Don't check connection if Supabase is not configured
    if (!isSupabaseConfigured()) {
      setConnectionStatus('unhealthy');
      setError('Supabase ist nicht konfiguriert. Bitte überprüfen Sie Ihre Umgebungsvariablen.');
      setLatency(null);
      setIsRetrying(false);
      return;
    }

    setIsRetrying(true);
    const result = await checkSupabaseHealth();

    if (result.isHealthy) {
      setConnectionStatus('healthy');
      setLatency(result.latency || null);
      setError(null);
    } else {
      setConnectionStatus('unhealthy');
      setError(result.error || 'Verbindung fehlgeschlagen');
      setLatency(null);
    }
    setIsRetrying(false);
  };

  useEffect(() => {
    // Initial check
    checkConnection();

    // Check every 30 seconds
    const interval = setInterval(checkConnection, 30000);

    // Also check when window regains focus
    const handleFocus = () => {
      console.log('[SupabaseConnectionMonitor] Window regained focus, checking connection');
      checkConnection();
    };
    window.addEventListener('focus', handleFocus);

    // Listen for HMR updates
    if (import.meta.hot) {
      const handleHMR = () => {
        console.log('[SupabaseConnectionMonitor] HMR update detected, verifying connection');
        // Small delay to allow Supabase client to reinitialize
        setTimeout(() => {
          checkConnection();
        }, 500);
      };

      import.meta.hot.on('vite:afterUpdate', handleHMR);
    }

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [location.pathname]);

  // Don't show anything if connection is healthy
  if (connectionStatus === 'healthy') {
    return null;
  }

  const handleRetry = () => {
    dataCache.clear(); // Clear cache to force fresh data
    checkConnection();
  };

  return (
    <div className={`fixed bottom-4 right-4 max-w-sm p-4 rounded-lg shadow-lg z-50 ${
      connectionStatus === 'unhealthy' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
    }`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          {connectionStatus === 'checking' ? (
            <RefreshCw className="h-5 w-5 text-yellow-600 animate-spin" />
          ) : connectionStatus === 'unhealthy' ? (
            <WifiOff className="h-5 w-5 text-red-600" />
          ) : (
            <CheckCircle className="h-5 w-5 text-green-600" />
          )}
        </div>
        <div className="flex-1">
          <h3 className={`text-sm font-medium ${
            connectionStatus === 'unhealthy' ? 'text-red-800' : 'text-yellow-800'
          }`}>
            {connectionStatus === 'checking' 
              ? 'Verbindung wird überprüft...'
              : 'Verbindungsproblem'
            }
          </h3>
          {error && (
            <p className="mt-1 text-sm text-gray-600">
              Datenbank nicht verbunden
            </p>
          )}
          {connectionStatus === 'unhealthy' && (
            <div className="mt-3">
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    Wird versucht...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Erneut versuchen
                  </>
                )}
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Klicken Sie auf "Connect to Supabase\" oben rechts
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};